import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button, TextInput } from '@c2fo/liquidity';
import { EyeOpenIcon, EyeCrossedIcon } from '@c2fo/liquidity/icons';
import { LoadingOverlay } from '../LoadingSpinner';
import { BackLink } from '../BackLink';
import { Card } from '../Card';
import { CardFooter } from '../CardFooter';
import { FormField } from '../FormField';
import { NotFoundCard } from '../NotFoundCard';
import { PageContainer } from '../PageContainer';
import { useCredentialsState } from '../../contexts';
import { updateCredential, uploadCredentialFile, getFieldConfigsForCredentialType } from '../../api/Credentials';
import { useErrorState } from '../ErrorBanner';
import { useApiContext } from '../../contexts/FileDeliveryContext';
import { CredentialTypeChip } from '../CredentialTypeChip';
import { CredentialFileField } from '../CredentialFileField';
import { FieldHelpTooltip } from '../FieldHelpTooltip';
import { AdvancedCredentialEditor } from '../AdvancedCredentialEditor';

interface PendingFileUpload {
  fieldKey: string;
  filename: string;
  content: string;
}

export const CredentialForm: React.FC = () => {
  const { credentialUid } = useParams<{ credentialUid: string }>();
  const navigate = useNavigate();
  const { addError } = useErrorState();
  const apiContext = useApiContext();
  const {
    currentCredential: credential,
    isLoadingCredential: isLoading,
    fetchCredential,
    clearCurrentCredential,
  } = useCredentialsState();

  const [displayName, setDisplayName] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [pendingFileUploads, setPendingFileUploads] = useState<PendingFileUpload[]>([]);
  const [editorMode, setEditorMode] = useState<'simple' | 'advanced'>('simple');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (credentialUid) {
      // Construct full path: credentials/{credentialUid}
      fetchCredential(`credentials/${credentialUid}`);
    }

    return () => {
      clearCurrentCredential();
    };
  }, [credentialUid, fetchCredential, clearCurrentCredential]);

  useEffect(() => {
    if (credential) {
      setDisplayName(credential.displayName);
      setValues({});
    }
  }, [credential]);

  const handleValueChange = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const toggleShowSecret = (key: string) => {
    setShowSecrets((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFileContent = (fieldKey: string, content: string, filename: string) => {
    setPendingFileUploads((prev) => {
      const filtered = prev.filter((f) => f.fieldKey !== fieldKey);

      return [...filtered, { fieldKey, filename, content }];
    });
  };

  const handleAdvancedValuesChange = (newValues: Record<string, string>) => {
    setValues(newValues);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!credential) return;

    setIsSaving(true);
    try {
      if (pendingFileUploads.length > 0) {
        for (const fileUpload of pendingFileUploads) {
          await uploadCredentialFile(apiContext, {
            name: credential.name,
            filename: values[fileUpload.fieldKey] || fileUpload.filename,
            content: fileUpload.content,
          });
        }
      }

      const updateMask = ['display_name'];
      const hasValueChanges = Object.values(values).some((v) => v.length > 0);
      if (hasValueChanges) {
        updateMask.push('values');
      }

      await updateCredential(apiContext, {
        credential: {
          name: credential.name,
          displayName,
          ...(hasValueChanges ? { values } : {}),
        },
        updateMask,
      });
      navigate(`/file-delivery/credentials/${credentialUid}`);
    } catch {
      addError({
        id: 'update-credential-error',
        message: 'Failed to update credential. File upload may have failed.',
        type: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <LoadingOverlay message="Loading credential..." />;
  }

  if (!credential) {
    return (
      <PageContainer>
        <BackLink to="/file-delivery/credentials" label="Back to Credentials" />
        <NotFoundCard message="Credential not found or could not be loaded." />
      </PageContainer>
    );
  }

  const fieldConfigs = getFieldConfigsForCredentialType(credential.credentialType);
  const existingKeys = credential.maskedValues ? Object.keys(credential.maskedValues) : [];

  return (
    <PageContainer>
      <BackLink to={`/file-delivery/credentials/${credentialUid}`} label="Back to Credential Details" />

      <form onSubmit={handleSubmit}>
        <Card>
          <div className="px-6 pt-4">
            <CredentialTypeChip credentialType={credential.credentialType} />
            <h2 className="text-lg font-medium mt-3 text-gray-900">Editing {credential.displayName}</h2>
          </div>
          <div className="px-6 pb-6 space-y-4">
            <FormField label="Display Name" required htmlFor="displayName">
              <TextInput
                id="displayName"
                className="w-full"
                value={displayName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisplayName(e.target.value)}
                placeholder="Enter display name"
                required
                size="sm"
              />
            </FormField>

            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Credential Values</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Leave fields empty to keep existing values. Enter new values to update.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setEditorMode('simple')}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      editorMode === 'simple'
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Simple
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditorMode('advanced')}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      editorMode === 'advanced'
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Advanced (JSON)
                  </button>
                </div>
              </div>

              {editorMode === 'advanced' ? (
                <AdvancedCredentialEditor
                  credentialType={credential.credentialType}
                  values={values}
                  onChange={handleAdvancedValuesChange}
                />
              ) : (
                <div className="space-y-4">
                  {fieldConfigs.map((field) => {
                    const hasExisting = existingKeys.includes(field.key);
                    const hasUserInput = (values[field.key] ?? '').length > 0;

                    if (field.type === 'filePath') {
                      return (
                        <div key={field.key}>
                          {hasExisting && <span className="text-xs text-green-600 mb-1 block">(currently set)</span>}
                          <CredentialFileField
                            label={field.label}
                            pathValue={values[field.key] || ''}
                            onPathChange={(path) => handleValueChange(field.key, path)}
                            onFileContent={(content, filename) => handleFileContent(field.key, content, filename)}
                            helpText={field.helpText}
                            helpSeverity={field.helpSeverity}
                            required={false}
                            placeholder={hasExisting ? 'Leave blank to keep current value' : field.placeholder}
                          />
                        </div>
                      );
                    }

                    if (field.type === 'secret') {
                      return (
                        <div key={field.key}>
                          <div className="flex items-center gap-1 mb-1">
                            <label htmlFor={field.key} className="block text-sm font-medium text-gray-700">
                              {field.label}
                              {hasExisting && <span className="ml-2 text-xs text-green-600">(currently set)</span>}
                            </label>
                            <FieldHelpTooltip helpText={field.helpText} severity={field.helpSeverity} />
                          </div>
                          <div className="relative">
                            <TextInput
                              id={field.key}
                              type={showSecrets[field.key] ? 'text' : 'password'}
                              value={values[field.key] ?? ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                handleValueChange(field.key, e.target.value)
                              }
                              placeholder={
                                hasExisting ? 'Leave blank to keep current value' : `Enter ${field.label.toLowerCase()}`
                              }
                              className="w-full pr-10"
                              size="sm"
                            />
                            {hasUserInput && (
                              <button
                                type="button"
                                tabIndex={-1}
                                onClick={() => toggleShowSecret(field.key)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                              >
                                {showSecrets[field.key] ? (
                                  <EyeOpenIcon className="h-5 w-5 stroke-2" />
                                ) : (
                                  <EyeCrossedIcon className="h-5 w-5 stroke-2" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={field.key}>
                        <div className="flex items-center gap-1 mb-1">
                          <label htmlFor={field.key} className="block text-sm font-medium text-gray-700">
                            {field.label}
                            {hasExisting && <span className="ml-2 text-xs text-green-600">(currently set)</span>}
                          </label>
                          <FieldHelpTooltip helpText={field.helpText} severity={field.helpSeverity} />
                        </div>
                        <TextInput
                          id={field.key}
                          value={values[field.key] ?? ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleValueChange(field.key, e.target.value)
                          }
                          placeholder={
                            hasExisting ? 'Leave blank to keep current value' : `Enter ${field.label.toLowerCase()}`
                          }
                          className="w-full"
                          size="sm"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <CardFooter>
            <Link to={`/file-delivery/credentials/${credentialUid}`}>
              <Button variant="tertiary" className="rounded-md" type="button">
                Cancel
              </Button>
            </Link>
            <Button variant="primary" className="rounded-md" type="submit" disabled={isSaving || !displayName}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </PageContainer>
  );
};
